import React, { useContext, useEffect, useRef, useState, useMemo } from 'react';
import { ActivityIndicator, FlatList, Platform, Pressable, ScrollView } from "react-native";
import { View, Text } from "@constants/Themed";
import { AntDesign, Feather } from '@expo/vector-icons';
import { themeSelector } from "@reduxLocal/selectors/themeSelector";
import * as Animatable from 'react-native-animatable';
import { ToolbarContext } from "@hooks/toolbarContext";
import { Avatar, Popover } from "@mui/material";
import SearchComponent from "@components/Global/SearchComponent/SearchBar";
import AnimatedLottieView from '@utils/lottieAnimation';
import { schemaCache } from '@utils/schemaCache';
import { Button, Dialog, Separator, SizableText, XStack, YStack, ZStack } from "tamagui";
import moment from "moment";
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { selectUser } from "@reduxLocal/selectors/userSelectors";
import { useQueryClient } from "@tanstack/react-query";
import styles, { getModernCardStyle, getModernActionStyle, getModernTextColor } from "@stylesheet";
import apis from "@api/api";
import GenerateSchemaForm from "@hooks/generateSchemaForm";
import RowComponent from "@components/Global/RowComponent/RowComponent";
import SwitchWithIcons from "react-native-switch-with-icons";
import CalendarSelect from "@components/CalendarSelect";
import { SmastSaaSWhite } from "@assets/--native";
import { useDispatch, useSelector } from "react-redux";
import { DefaultRootState } from "@reduxLocal/persistState";
import { CONTEXT_SIZE_TOGGLE } from "@reduxLocal/actions/types";

const CalLoadingIndicator = () => {
  const calLoading = useSelector((state: DefaultRootState) => state.UI.calendarLoading);
  return calLoading && (<ActivityIndicator color="white" style={{ marginHorizontal: 20 }} />)
}

export const CalendarHeader = () => {
  const { calendarContext, calendarHeader } = useContext(ToolbarContext);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [showUsers, setShowUsers] = useState(null);
  const theme = themeSelector();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const settingsRef = useRef(null);
  const [filter, setFilter] = useState(false);
  const [addSSCal, setAddSSCal] = useState(false);
  const [activeItem, setActiveItem] = useState<any>(null);
  const addCalRef = useRef(null);

  // Normalize calendarContext to ensure it's a valid moment object (required by AdapterMoment)
  const normalizedCalendarContext = useMemo(() => {
    if (!calendarContext) return moment();
    if (moment.isMoment(calendarContext)) return calendarContext;
    if (calendarContext instanceof Date) return moment(calendarContext);
    if (typeof calendarContext === 'string') {
      // Try parsing as ISO string first
      const parsed = moment(calendarContext, moment.ISO_8601, true);
      if (parsed.isValid()) return parsed;
      // If it's already formatted like "December 2025", try to parse it
      const monthYearParsed = moment(calendarContext, "MMMM YYYY", true);
      if (monthYearParsed.isValid()) return monthYearParsed;
      // Fallback to current date
      return moment();
    }
    return moment(calendarContext);
  }, [calendarContext]);

  // Use cached schema
  const schema = schemaCache.getSchema('wholeCalendarSchema'); 

  const handleChange = (value) => {
    setFilter(value);
  }

  useEffect(() => {
    queryClient.prefetchQuery({ queryKey: ['users'] });
    //dispatch current date to redux
  }, [])

  return (
    <View style={{ flexDirection: "row", overflow: "visible", backgroundColor: "transparent" }}>
      <Popover
        open={!!showUsers}
        anchorEl={showUsers}
        style={{ minWidth: 300, minHeight: 600, bottom: 100 }}
        onClose={() => setShowUsers(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <FlatList
          data={selectUser()?.hierarchy}
          ListEmptyComponent={<Text style={{ padding: 20 }}>No other users are currently sharing calendars</Text>}
          renderItem={({ item }) => {
            return (
              <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", padding: 10 }}>
                <Avatar src={item.avatar?.img || "https://storage.googleapis.com/smartsaas-avatar-store/Asset%202.png"} />
                <Text>{item.name}</Text>
              </View>
            )
          }}
          keyExtractor={(item) => item._id}
        />
      </Popover>
      <View style={{ minWidth: "97%", minHeight: 80, marginLeft: Platform.OS === 'web' ? 20 : undefined, shadowRadius: 10, backgroundColor: theme ? 'rgb(90,192,234)' : 'grey', shadowOpacity: 0.2, flexDirection: 'row', borderBottomStartRadius: 20, gap: 10, borderBottomEndRadius: 20, justifyContent: 'space-between', alignItems: 'center', padding: 20 }}>
        <View style={{ flex: 2, overflow: "visible" }}>
          <Animatable.View transition={'minHeight'} style={{ minWidth: "50%", left: -20, alignSelf: "stretch", borderBottomStartRadius: 20, paddingHorizontal: 30, top: -30, position: 'absolute', flex: 1.5, backgroundColor: theme ? "rgb(90,192,234)" : "grey", minHeight: calendarOpen ? 300 : 0 }}>
            {
              calendarOpen &&
              <DateCalendar
                sx={{ color: "white", textAlign: "center", scale: 1.3, marginBlock: 20 }}
                defaultValue={normalizedCalendarContext}
                views={['month', 'year']}
                openTo="month"
                onYearChange={(date) => { setCalendarOpen(false); calendarHeader.setMonth?.(moment.isMoment(date) ? date.toDate() : date) }}
                onMonthChange={(date) => { setCalendarOpen(false); calendarHeader.setMonth?.(moment.isMoment(date) ? date.toDate() : date) }}
              />
            }
            <View style={{ alignSelf: "stretch", minWidth: "100%", flexDirection: "row", flex: 1, marginRight: 20, paddingBottom: 10 }}>
              <XStack style={{ flex: 1, minWidth: 100, marginRight: 20, maxWidth: 100, flexDirection: "row", paddingRight: 20, top: 5 }}>
                <Button style={{ borderRadius: 20, backgroundColor: theme ? "white" : "#333333" }} icon={<AntDesign name="left" color={theme ? "black" : "#ececec"} />} borderRadius={20} borderStartEndRadius={0} borderEndEndRadius={0} borderTopRightRadius={0} borderBottomRightRadius={0} onPress={() => calendarHeader.decrementMonth()} />
                <Button style={{ backgroundColor: theme ? "white" : "#333333" }} icon={<AntDesign name="right" color={theme ? "black" : "white"} />} borderRadius={20} borderStartStartRadius={0} borderEndStartRadius={0} borderTopLeftRadius={20} borderBottomLeftRadius={20} onPress={() => { calendarHeader.incrementMonth() }} />
              </XStack>
              <Separator vertical borderColor={'white'} style={{ marginRight: 20 }} />
              {
                window.innerWidth > 1200 &&
                <SizableText adjustsFontSizeToFit style={{ alignSelf: "stretch", top: 25, paddingTop: 15, fontSize: 20, textJustify: 'auto', textAlign: "center", verticalAlign: 'middle', flex: 2, color: "white", fontWeight: "bold" }}>{moment(normalizedCalendarContext).format("MMMM YYYY")}</SizableText>
              }
              <AntDesign name="calendar" size={36} color={"white"} style={{ textAlign: "right", flex: 0.5, borderRadius: 50, padding: 7, }} onPress={() => setCalendarOpen(!calendarOpen)} />
              <CalLoadingIndicator />
            </View>
          </Animatable.View>
        </View>
        {
          !moment(normalizedCalendarContext).isSame(moment(), 'month') &&
          <Button onPress={() => {
            calendarHeader.setMonth?.(moment().toDate())
          }} style={[styles.centerEverything, { flex: 1, flexDirection: "row", alignSelf: "stretch", backgroundColor: "rgb(90,192,234)", padding: 10, borderRadius: 20 }]}>
              <AntDesign name="backward" size={18} color={"white"} />
            <Text style={{ color: "white" }}>Today</Text>
          </Button>
        }
        <View style={{ flex: 4, alignItems: "flex-end", justifyContent: "flex-end", alignContent: "flex-end" }}>
          <SearchComponent
            removeFlex customIcons={[
            { functionName: 'calendar', function: () => dispatch({ type: CONTEXT_SIZE_TOGGLE }), icon: <AntDesign name="calendar" size={18} color={"white"} style={{ borderRadius: 50, padding: 7, }} /> },
            { functionName: 'google-calendar', function: () => calendarHeader.showAddGoogleCal(), icon: < AnimatedLottieView source={require('@assets/animations/google_calendar.json')} style={{ margin: -5, width: 40 }} autoPlay /> },
            {
              functionName: 'calendarSettings', function: () => setDialogOpen(true), icon: <Feather name="settings" size={18} color="white" style={{ borderRadius: 50, padding: 7, }} />
            },
            {
              functionName: 'ssCalendar', function: () => setAddSSCal(true), icon: <ZStack><SmastSaaSWhite style={{ position: "absolute", maxHeight: 20, maxWidth: 20, top: -10, left: -10 }} /><AntDesign name="plus" size={10} style={{ backgroundColor: "rgb(90,192,234)", padding: 3, borderRadius: 5, zIndex: 100, position: "absolute", top: 0, right: -15 }} color={"white"} /></ZStack>
            }
          ]} />
        </View>
        <Dialog key="calSettings" open={dialogOpen}>
          <Dialog.Portal>
            <Dialog.Overlay
              animation="quick"
              opacity={0.5}
              style={{ zIndex: 1000, padding: 50 }}
              enterStyle={{ opacity: 0 }}
              exitStyle={{ opacity: 0 }}
            />
            <Dialog.Content
              bordered
              elevate
              animation={[
                'quick',
                {
                  opacity: {
                    overshootClamping: true,
                  },
                },
              ]}
              enterStyle={{ x: 0, y: -20, opacity: 0, scale: 0.9 }}
              exitStyle={{ x: 0, y: 10, opacity: 0, scale: 0.95 }}
              x={0}
              scale={1}
              style={[
                getModernCardStyle(theme, 'large'),
                {
                  padding: 40,
                  minWidth: 500,
                  maxWidth: 600,
                  maxHeight: "85%",
                  gap: 20
                }
              ]}
              opacity={1}
              y={0}
            >
              <Dialog.Title fontSize={32} fontWeight={'bold'} style={{ color: getModernTextColor(theme) }}>Calendar Settings</Dialog.Title>
              <Dialog.Description fontSize={18} fontWeight="bold" style={{ color: getModernTextColor(theme, 'secondary') }}>
                {activeItem?.title || activeItem?.calendar?.title || ""}
              </Dialog.Description>
              <Dialog.Description style={{ color: getModernTextColor(theme, 'muted') }}>
                Change your calendar settings for bookings and meetings
              </Dialog.Description>
              <Separator marginVertical={20} borderColor={theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.2)'} />
              <YStack flex={1}>
                <CalendarSelect />
              </YStack>
              <View style={{ maxHeight: 160, gap: 30, marginTop: 20 }}>
                <RowComponent forceRow title="Availability" value={filter ? "Edit" : "View"} customComponent={(
                  <SwitchWithIcons
                    value={filter}
                    trackColor={{ false: '#58BFEA', true: '#58BFEA' }}
                    thumbColor={{ false: 'grey', true: 'grey' }}
                    animationDuration={150}
                    style={{ flex: 1, alignSelf: 'stretch', justifyContent: 'center' }}
                    icon={{ false: 'eye', true: 'edit' }} onValueChange={(value) => handleChange(value)} />
                )} />
              </View>
              <YStack overflow="scroll" marginVertical={30} flex={6}>
              {
                filter ? 
                  <GenerateSchemaForm
                    key={filter + "calsettings"}
                    ref={settingsRef}
                    schema={[
                      {
                        title: "Weekdays",
                        type: 'timeslider',
                        validation: {
                          'step': 1,
                          'maxValue': 24,
                          'minValue': 0
                        }
                      },
                      {
                        title: "Weekends",
                        type: 'timeslider',
                        validation: {
                          'step': 1,
                          'maxValue': 24,
                          'minValue': 0
                        }
                      }
                    ]}
                    initialData={undefined}
                    submitFunction={async (vals) => {
                      await apis.updateCalendarSettings(vals, activeItem.id)
                    }}
                    removeSubmit
                  /> : <GenerateSchemaForm
                    key={filter + "calsettings"}
                    ref={settingsRef}
                    schema={[
                      {
                        title: "Monday",
                        type: 'timeslider',
                        validation: {
                          'step': 1,
                          'maxValue': 24,
                          'minValue': 0
                        }
                      },
                      {
                        title: "Tuesday",
                        type: 'timeslider',
                        validation: {
                          'step': 1,
                          'maxValue': 24,
                          'minValue': 0
                        }
                      },
                      {
                        title: "Wednesday",
                        type: 'timeslider',
                        validation: {
                          'step': 1,
                          'maxValue': 24,
                          'minValue': 0
                        }
                      },
                      {
                        title: "Thursday",
                        type: 'timeslider',
                        validation: {
                          'step': 1,
                          'maxValue': 24,
                          'minValue': 0
                        }
                      },
                      {
                        title: "Friday",
                        type: 'timeslider',
                        validation: {
                          'step': 1,
                          'maxValue': 24,
                          'minValue': 0
                        }
                      },
                      {
                        title: "Saturday",
                        type: 'timeslider',
                        validation: {
                          'step': 1,
                          'maxValue': 24,
                          'minValue': 0
                        }
                      },
                      {
                        title: "Sunday",
                        type: 'timeslider',
                        validation: {
                          'step': 1,
                          'maxValue': 24,
                          'minValue': 0
                        }
                      }]}
                    initialData={undefined}
                    submitFunction={async (vals) => {
                      await apis.updateCalendarSettings(vals, activeItem.id)
                    }}
                    removeSubmit
                  />
              }
              </YStack>
              <XStack flex={1} gap={"$3"}>
                <Dialog.Close asChild>
                  <Button
                    width="50%"
                    style={getModernActionStyle(theme, 'grey')}
                    onPress={() => {
                      setDialogOpen(false);
                    }}
                  >
                    Close
                  </Button>
                </Dialog.Close>
                <Dialog.Close asChild>
                  <Button
                    width="50%"
                    style={getModernActionStyle(theme, 'blue')}
                    onPress={() => {
                      settingsRef.current?.submitForm();
                      setDialogOpen(false);
                    }}
                  >
                    Save
                  </Button>
                </Dialog.Close>
              </XStack>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog>
        <Dialog key="addNewCal" open={addSSCal}>
          <Dialog.Portal>
            <Dialog.Overlay
              animation="quick"
              opacity={0.5}
              style={{ zIndex: 1000, padding: 50 }}
              enterStyle={{ opacity: 0 }}
              exitStyle={{ opacity: 0 }}
            />
            <Dialog.Content
              bordered
              elevate
              animation={[
                'quick',
                {
                  opacity: {
                    overshootClamping: true,
                  },
                },
              ]}
              enterStyle={{ x: 0, y: -20, opacity: 0, scale: 0.9 }}
              exitStyle={{ x: 0, y: 10, opacity: 0, scale: 0.95 }}
              x={0}
              scale={1}
              style={[
                getModernCardStyle(theme, 'large'),
                {
                  gap: 20,
                  padding: 40,
                  minWidth: 500,
                  maxWidth: 600,
                  maxHeight: "90%"
                }
              ]}
              opacity={1}
              y={0}
            >
              <Dialog.Title fontSize={32} fontWeight={'bold'} style={{ color: getModernTextColor(theme) }}>
                SmartSaaS Calendars
              </Dialog.Title>
              <Dialog.Description style={{ color: getModernTextColor(theme, 'muted') }}>
                Create your very own SmartSaaS calendar.
              </Dialog.Description>
              <Separator marginVertical={20} borderColor={theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.2)'} />
              <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{paddingLeft: 20}} style={{ gap: 30, maxHeight: "80%", alignSelf: "center" }}>
                <GenerateSchemaForm
                  submitFunction={async (v: any) => await apis.addSSCal({ ...v, type: "smartsaas" }).then(() => {
                    setAddSSCal(false);
                  })}
                  hideButton
                  customStyling={{ left: -15 }}
                  ref={addCalRef}
                  scrollEnabled={false}
                  callback={{ setState: setAddSSCal, state: addSSCal }}
                  schemaName="calendar"
                  nullify={['availability', 'billingWarning', 'companyId', 'reminders', 'timezone', 'type', 'calendar', 'device', 'userId', 'calendarId']}
                  schema={schema} />
              </ScrollView>
              <XStack gap={"$3"}>
                <Dialog.Close asChild>
                  <Button
                    style={getModernActionStyle(theme, 'red')}
                    minWidth={100}
                    onPress={() => {
                      setAddSSCal(false);
                    }}
                  >
                    Close
                  </Button>
                </Dialog.Close>
                <Dialog.Close asChild>
                  <Button
                    style={getModernActionStyle(theme, 'green')}
                    minWidth={100}
                    onPress={() => {
                      addCalRef.current?.submitForm();
                    }}
                  >
                    Create
                  </Button>
                </Dialog.Close>
              </XStack>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog>
        <Pressable>
       
        </Pressable>
      </View>
    </View>
  )
}


